import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  theme,
  Typography,
} from 'antd';
import { useState } from 'react';
import type { ChangeEvent, ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StudyLevel,
  StudyLineType,
  TeachingLanguage,
} from '../../Types/interfaces';
import { EmailListFormComponent } from './EmailListFormComponent';
import type { MasterDetailItem } from './SetupMasterDetail';
import { SetupMasterDetail } from './SetupMasterDetail';
import { getDefaultNumberOfYears } from './universitySetupUtils';

interface StudyLineFormComponentProps {
  departmentNames: RefObject<Array<string>>;
  studyLineNames: RefObject<Array<Array<string>>>;
}

interface StudyLineValue {
  studyLineName?: string;
  studyLineLevel?: StudyLevel | null;
  studyLineType?: StudyLineType | null;
  teachingLanguage?: TeachingLanguage | null;
  numberOfYears?: number | null;
  coordinators?: Array<string | undefined>;
}

// The longest study lines, e.g. medicine, last 6 years
const MAX_NUMBER_OF_YEARS = 6;

const isComplete = (studyLine: StudyLineValue): boolean =>
  !!studyLine?.studyLineName?.trim() &&
  studyLine.studyLineLevel !== undefined &&
  studyLine.studyLineLevel !== null &&
  studyLine.studyLineType !== undefined &&
  studyLine.studyLineType !== null &&
  !!studyLine.teachingLanguage &&
  !!studyLine.numberOfYears &&
  (studyLine.coordinators ?? []).some((email) => !!email?.trim());

// The study lines of every department: the departments are listed on the left, with their status at a glance,
// and the study lines of the selected one are edited on the right
export const StudyLineFormComponent = ({
  departmentNames,
  studyLineNames,
}: StudyLineFormComponentProps): ReactNode => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const form = Form.useFormInstance();
  const [selected, setSelected] = useState(0);
  const watchedStudyLines = Form.useWatch(['studyLines'], {
    // the lists of the items not shown are not mounted, their values are still needed for the status
    preserve: true,
  }) as StudyLineValue[][] | undefined;
  const studyLines = watchedStudyLines ?? [];

  const handleNameChange = (
    event: ChangeEvent<HTMLInputElement>,
    i: number,
    j: number
  ): void => {
    studyLineNames.current[i][j] = event.target.value; //eslint-disable-line
  };

  // The sessions are listed one after the other for the study lines of all departments,
  // so the sessions of a study line are found by its position in that list
  const firstSessionIndex = (departmentIndex: number): number =>
    ((form.getFieldValue('studyLines') as unknown[][] | undefined) ?? [])
      .slice(0, departmentIndex)
      .reduce((count, lines) => count + (lines?.length ?? 0), 0);

  const updateSessions = (update: (sessions: unknown[]) => void): void => {
    const sessions = [
      ...((form.getFieldValue('sessions') as unknown[] | undefined) ?? []),
    ];
    update(sessions);
    form.setFieldValue('sessions', sessions);
  };

  // A new study line is added at the top, next to the button, with an empty list of sessions
  const addStudyLine = (
    departmentIndex: number,
    add: (defaultValue?: unknown, insertIndex?: number) => void
  ): void => {
    const position = firstSessionIndex(departmentIndex);
    updateSessions((sessions) => {
      while (sessions.length < position) {
        sessions.push([]);
      }
      sessions.splice(position, 0, []);
    });

    const names = studyLineNames.current;
    while (names.length <= departmentIndex) {
      names.push([]);
    }
    names[departmentIndex].unshift('');

    add(undefined, 0);
  };

  // Choosing the study level fills in its usual number of years, unless the rector set another one:
  // an empty number, or the usual one of the other level, is replaced
  const setDefaultNumberOfYears = (
    departmentIndex: number,
    lineIndex: number,
    studyLevel: StudyLevel
  ): void => {
    const path = ['studyLines', departmentIndex, lineIndex, 'numberOfYears'];
    const numberOfYears = form.getFieldValue(path) as number | null | undefined;
    const otherLevel =
      studyLevel === StudyLevel.Master
        ? StudyLevel.Bachelor
        : StudyLevel.Master;
    if (
      !numberOfYears ||
      numberOfYears === getDefaultNumberOfYears(otherLevel)
    ) {
      form.setFieldValue(path, getDefaultNumberOfYears(studyLevel));
    }
  };

  // Deleting a study line deletes its sessions too, so the sessions of the other study lines stay in place
  const deleteStudyLine = (
    departmentIndex: number,
    lineIndex: number,
    remove: (index: number) => void
  ): void => {
    const position = firstSessionIndex(departmentIndex) + lineIndex;
    updateSessions((sessions) => {
      sessions.splice(position, 1);
    });
    studyLineNames.current[departmentIndex]?.splice(lineIndex, 1);
    remove(lineIndex);
  };

  const items: MasterDetailItem[] = (departmentNames.current ?? []).map(
    (departmentName, index) => {
      const lines = studyLines[index] ?? [];
      let warning: string | undefined;
      if (lines.length === 0) {
        warning = t('uniForm.masterDetail.noStudyLines');
      } else if (!lines.every(isComplete)) {
        warning = t('uniForm.masterDetail.incomplete');
      }

      return {
        key: index,
        label: departmentName || '-',
        summary:
          lines.length === 0
            ? t('uniForm.masterDetail.noStudyLines')
            : t('uniForm.masterDetail.studyLinesSummary', {
                count: lines.length,
              }),
        warning,
      };
    }
  );

  if (items.length === 0) {
    return null;
  }
  const index = Math.min(selected, items.length - 1);

  return (
    <SetupMasterDetail
      items={items}
      selectedKey={index}
      onSelect={setSelected}
      title={items[index].label}
      subtitle={t('uniForm.studyLinesLabel')}
    >
      <Form.List name={['studyLines', index]} key={index}>
        {(fields, fieldOperations) => (
          <>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ marginBottom: 16 }}
              onClick={() => addStudyLine(index, fieldOperations.add)}
            >
              {t('uniForm.slineComp.addStudyLine')}
            </Button>
            {fields.map((field) => (
              <Card
                key={field.key}
                size="small"
                style={{ marginBottom: 16 }}
                styles={{ header: { background: token.colorInfoBg } }}
                title={
                  studyLines[index]?.[field.name]?.studyLineName?.trim() ||
                  t('uniForm.slineComp.cardTitle', { number: field.name + 1 })
                }
                extra={
                  <Popconfirm
                    title={t('uniForm.slineComp.deleteStudyLineTitle')}
                    description={t(
                      'uniForm.slineComp.deleteStudyLineDescription'
                    )}
                    okText={t('uniForm.depComp.deleteDepartmentOk')}
                    cancelText={t('uniForm.depComp.deleteDepartmentCancel')}
                    okButtonProps={{ danger: true }}
                    onConfirm={() =>
                      deleteStudyLine(index, field.name, fieldOperations.remove)
                    }
                  >
                    <Button
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      aria-label={t('uniForm.slineComp.deleteStudyLine')}
                      title={t('uniForm.slineComp.deleteStudyLine')}
                    />
                  </Popconfirm>
                }
              >
                <Form.Item
                  label={t('uniForm.slineComp.name')}
                  tooltip={t('uniForm.help.studyLineName')}
                  rules={[
                    {
                      required: true,
                      message: t('validator.studyLineNameEmpty'),
                    },
                    {
                      validator: (_, value): Promise<void> => {
                        return value === '' ||
                          studyLineNames.current[index].filter(
                            (name) => name === value
                          ).length === 1
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(t('validator.slineNameUsed'))
                            );
                      },
                    },
                  ]}
                  name={[field.name, 'studyLineName']}
                >
                  <Input
                    onChange={(event) =>
                      handleNameChange(event, index, field.name)
                    }
                  />
                </Form.Item>
                <Form.Item
                  label={t('uniForm.slineComp.studyLevel')}
                  tooltip={t('uniForm.help.studyLevel')}
                  rules={[
                    {
                      required: true,
                      message: t('validator.studyLineStudyLevelEmtpy'),
                    },
                  ]}
                  name={[field.name, 'studyLineLevel']}
                >
                  <Select<StudyLevel>
                    onChange={(studyLevel) =>
                      setDefaultNumberOfYears(index, field.name, studyLevel)
                    }
                    options={[
                      {
                        value: StudyLevel.Bachelor,
                        label: t('uniForm.studyLevels.bachelor'),
                      },
                      {
                        value: StudyLevel.Master,
                        label: t('uniForm.studyLevels.master'),
                      },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  label={t('uniForm.slineComp.studyLineType')}
                  tooltip={t('uniForm.help.studyLineType')}
                  rules={[
                    {
                      required: true,
                      message: t('validator.studyLineTypeEmpty'),
                    },
                  ]}
                  name={[field.name, 'studyLineType']}
                >
                  <Select
                    options={[
                      {
                        value: StudyLineType.ComputerScience,
                        label: t('uniForm.studyLineTypes.computerScience'),
                      },
                      {
                        value: StudyLineType.Math,
                        label: t('uniForm.studyLineTypes.math'),
                      },
                      {
                        value: StudyLineType.MathComputerScience,
                        label: t('uniForm.studyLineTypes.mathComputerScience'),
                      },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  label={t('uniForm.slineComp.teachingLanguage')}
                  tooltip={t('uniForm.help.teachingLanguage')}
                  rules={[
                    {
                      required: true,
                      message: t('validator.studyLineTeachingLanguageEmpty'),
                    },
                  ]}
                  name={[field.name, 'teachingLanguage']}
                >
                  <Select
                    options={[
                      {
                        value: TeachingLanguage.Romanian,
                        label: t('uniForm.teachingLanguages.romanian'),
                      },
                      {
                        value: TeachingLanguage.English,
                        label: t('uniForm.teachingLanguages.english'),
                      },
                      {
                        value: TeachingLanguage.Hungarian,
                        label: t('uniForm.teachingLanguages.hungarian'),
                      },
                      {
                        value: TeachingLanguage.German,
                        label: t('uniForm.teachingLanguages.german'),
                      },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  label={t('uniForm.slineComp.numberOfYears')}
                  tooltip={t('uniForm.help.numberOfYears')}
                  rules={[
                    {
                      required: true,
                      message: t('validator.studyLineNumberOfYearsEmpty'),
                    },
                  ]}
                  name={[field.name, 'numberOfYears']}
                >
                  <InputNumber
                    min={1}
                    max={MAX_NUMBER_OF_YEARS}
                    precision={0}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Typography.Text type="secondary">
                  {t('uniForm.help.coordinators')}
                </Typography.Text>
                <EmailListFormComponent
                  propertyReference={'coordinators'}
                  parentReference={[field.name]}
                  buttonTitle={t('uniForm.slineComp.addCoordinator')}
                  emailType={t('uniForm.slineComp.coordinatorEmailType')}
                  requiredMessage={t('validator.coordinatorRequired')}
                />
              </Card>
            ))}
          </>
        )}
      </Form.List>
    </SetupMasterDetail>
  );
};
